import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { sized } from "@/lib/content";
import { MARKET } from "@/lib/format";
import { Reveal } from "@/components/public/Reveal";
import { PageHead, CTA, Img, TextLink } from "@/components/public/ui";
import { ProjectTile } from "@/components/public/ProjectCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const FILTERS = [["alle", "Alle"], ["industri", "Industri"], ["samferdsel", "Samferdsel"], ["bygg_naering", "Bygg og næring"], ["service_bustad", "Service og bustad"]];

function FeatureRow({ project }) {
  return (
    <Reveal className="grid gap-6 lg:grid-cols-12 lg:gap-10" data-testid="public-feature-project">
      <Link to={`/prosjekt/${project.slug}`} className="group block lg:col-span-8" data-testid="public-project-tile">
        <div className="overflow-hidden rounded-pub bg-muted">
          <div className="aspect-[4/3] sm:aspect-[16/10]">
            <Img src={sized(project.hero_image, 1800)} alt="" sizes="100vw" loading="eager" className="tile-img img-premium h-full w-full object-cover" />
          </div>
        </div>
      </Link>
      <div className="flex flex-col justify-between gap-6 lg:col-span-4 lg:py-1">
        <div>
          <p className="t-mono">{MARKET[project.market]} · {project.location} · {project.year}</p>
          <h2 className="t-h2 mt-4 text-ink"><Link to={`/prosjekt/${project.slug}`} className="hover:text-ink/80">{project.title}</Link></h2>
          <p className="mt-4 text-[16px] leading-relaxed text-muted-foreground">{project.subtitle}</p>
          <p className="mt-4 text-[14px] text-muted-foreground">Kunde: {project.client}</p>
        </div>
        <TextLink to={`/prosjekt/${project.slug}`}>Sjå prosjektet</TextLink>
      </div>
    </Reveal>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    api.get("/public/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const all = projects || [];
  const list = all.filter((p) => filter === "alle" || p.market === filter);
  const count = (k) => (k === "alle" ? all.length : all.filter((p) => p.market === k).length);
  const visible = FILTERS.filter(([k]) => k === "alle" || count(k) > 0);
  const [first, ...rest] = list;

  return (
    <div data-testid="public-projects-page">
      <PageHead eyebrow="Prosjekt" title="Nokre av prosjekta våre" lead="Kvinnherad Elektro har gjennom mange år vore med på prosjekt innan industri, samferdsel og bygg på Vestlandet.">
      </PageHead>

      <section className="container-public mt-10 sm:mt-14">
        <div className="scrollbar-none -mx-5 flex gap-1 overflow-x-auto border-b border-border px-5 sm:mx-0 sm:px-0" data-testid="public-projects-filters" role="tablist" aria-label="Filtrer prosjekt">
          {visible.map(([k, l]) => (
            <button
              key={k}
              role="tab"
              aria-selected={filter === k}
              onClick={() => setFilter(k)}
              data-testid={`public-projects-filter-${k}`}
              className={cn(
                "-mb-px inline-flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-[15px] font-medium transition-colors duration-150 first:pl-0",
                filter === k ? "border-ink text-ink" : "border-transparent text-ink/55 hover:text-ink"
              )}
            >
              {l}
              <span className={cn("tabular font-mono text-[12px]", filter === k ? "text-ink/60" : "text-muted-foreground/70")}>{count(k)}</span>
            </button>
          ))}
        </div>

        <div className="pt-10" data-testid="public-projects-grid">
          {projects === null && (
            <div className="grid gap-5 lg:grid-cols-12">
              <Skeleton className="aspect-[16/10] w-full rounded-pub lg:col-span-8" />
              <div className="lg:col-span-4"><Skeleton className="h-8 w-3/4" /><Skeleton className="mt-3 h-4 w-1/2" /></div>
            </div>
          )}
          {projects && list.length === 0 && <p className="text-muted-foreground" data-testid="public-projects-empty">Ingen prosjekt å vise akkurat no.</p>}
          {first && <FeatureRow key={first.slug} project={first} />}
          {rest.length > 0 && (
            <div className="mt-12 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 0.05}>
                  <ProjectTile project={p} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="pt-24 sm:pt-32">
        <CTA />
      </div>
    </div>
  );
}
